import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Shield, ShieldCheck, ShieldOff, Copy, Eye, EyeOff, Loader2 } from "lucide-react";
import { API } from "../App";



const TwoFactorSetup = ({ user, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState("initial"); // initial, setup, verify, disable
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`);
      setTwoFactorStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch 2FA status:", error);
    }
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open) {
      fetchStatus();
      setStep("initial");
      setSetupData(null);
      setVerifyCode("");
      setDisablePassword("");
      setDisableCode("");
    }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(response.data);
      setStep("setup");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la configuration 2FA");
    }
    setLoading(false);
  };

  const verifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error("Veuillez entrer un code à 6 chiffres");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: verifyCode });
      toast.success("2FA activé avec succès!");
      setStep("complete");
      onStatusChange?.(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide");
    }
    setLoading(false);
  };

  const disable2FA = async () => {
    if (!disablePassword || !disableCode) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, {
        password: disablePassword,
        code: disableCode
      });
      toast.success("2FA désactivé avec succès");
      setIsOpen(false);
      onStatusChange?.(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la désactivation");
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  const copyAllBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join("\n"));
      toast.success("Codes de secours copiés");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-start">
          <Shield className="h-4 w-4 mr-2" />
          Authentification à deux facteurs (2FA)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-brand-600" />
            Authentification 2FA
          </DialogTitle>
          <DialogDescription>
            Protégez votre compte avec une couche de sécurité supplémentaire
          </DialogDescription>
        </DialogHeader>

        {step === "initial" && (
          <div className="space-y-4">
            {twoFactorStatus?.enabled ? (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <ShieldCheck className="h-5 w-5" />
                    <span className="font-semibold">2FA est activé</span>
                  </div>
                  <p className="text-sm text-green-600">
                    Codes de secours restants: {twoFactorStatus.backup_codes_remaining}
                  </p>
                  <Button 
                    variant="destructive" 
                    className="mt-4 w-full"
                    onClick={() => setStep("disable")}
                  >
                    <ShieldOff className="h-4 w-4 mr-2" />
                    Désactiver 2FA
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-600 mb-4">
                    L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire 
                    en exigeant un code de votre application d'authentification lors de la connexion.
                  </p>
                  <Button 
                    className="w-full bg-brand-600 hover:bg-brand-700"
                    onClick={startSetup}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-4 w-4 mr-2" />
                    )}
                    Configurer 2FA
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {step === "setup" && setupData && (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Scannez ce QR code avec votre application d'authentification 
                (Google Authenticator, Authy, etc.)
              </p>
              <div className="bg-white p-4 rounded-lg border inline-block">
                <img 
                  src={`data:image/png;base64,${setupData.qr_code}`} 
                  alt="QR Code 2FA"
                  className="w-48 h-48"
                />
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Ou entrez ce code manuellement:</p>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono bg-white px-2 py-1 rounded border flex-1">
                  {setupData.secret}
                </code>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => copyToClipboard(setupData.secret)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Entrez le code à 6 chiffres de votre application:
              </label>
              <Input
                type="text"
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-2xl tracking-widest"
                maxLength={6}
              />
            </div>

            <Button 
              className="w-full bg-brand-600 hover:bg-brand-700"
              onClick={verifySetup}
              disabled={loading || verifyCode.length !== 6}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Vérifier et activer
            </Button>
          </div>
        )}

        {step === "complete" && setupData && (
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <ShieldCheck className="h-5 w-5" />
                <span className="font-semibold">2FA activé avec succès!</span>
              </div>
            </div>

            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>Codes de secours</span>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setShowBackupCodes(!showBackupCodes)}
                  >
                    {showBackupCodes ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </CardTitle>
                <CardDescription className="text-xs">
                  Conservez ces codes en lieu sûr. Ils vous permettront de vous connecter si vous perdez accès à votre application d'authentification.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {showBackupCodes ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      {setupData.backup_codes.map((code, i) => (
                        <code key={i} className="text-xs font-mono bg-white px-2 py-1 rounded border text-center">
                          {code}
                        </code>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full mt-2"
                      onClick={copyAllBackupCodes}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copier tous les codes
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-amber-700">Cliquez sur l'œil pour afficher les codes</p>
                )}
              </CardContent>
            </Card>

            <Button 
              className="w-full"
              onClick={() => setIsOpen(false)}
            >
              Terminé
            </Button>
          </div>
        )}

        {step === "disable" && (
          <div className="space-y-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <p className="text-sm text-red-700 mb-4">
                  ⚠️ La désactivation de 2FA rendra votre compte moins sécurisé.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Mot de passe actuel:</label>
                    <Input
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      placeholder="Votre mot de passe"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Code 2FA:</label>
                    <Input
                      type="text"
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setStep("initial")}
              >
                Annuler
              </Button>
              <Button 
                variant="destructive" 
                className="flex-1"
                onClick={disable2FA}
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Désactiver
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorSetup;
